// src/pages/PortfolioPage.tsx

import React, { FC } from 'react';
import styles from '../css/PortfolioPage.module.css';
import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
    ShoppingCart, 
    ArrowRight, 
    Gavel, 
    Box, 
    Layers, 
    TrendingUp,
    Cpu,
    ArrowUpRight
} from 'lucide-react';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

interface ProductFeature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const PortfolioPage: FC = () => {
    
    const otaErpFeatures: ProductFeature[] = [
        { 
            icon: <Box size={24} strokeWidth={1.5} />, 
            title: "Unified Core", 
            description: "Finance, SCM, and HCM running on a single, immutable database source for absolute data consistency." 
        },
        { 
            icon: <Layers size={24} strokeWidth={1.5} />, 
            title: "Process Engine", 
            description: "No-code automation layer allowing operations teams to design complex business logic workflows." 
        },
        { 
            icon: <Gavel size={24} strokeWidth={1.5} />, 
            title: "Legal AI (SUIT)", 
            description: "Integrated intelligence for real-time compliance monitoring and contract lifecycle management." 
        },
        { 
            icon: <ShoppingCart size={24} strokeWidth={1.5} />, 
            title: "Omnichannel Commerce", 
            description: "Direct headless connection between online sales channels and backend inventory systems." 
        },
    ];

    const futureVentures = [
        { 
            title: "Project F: FinTech Core", 
            description: "Algorithmic treasury management and automated securities analysis for corporate finance.", 
            icon: <TrendingUp size={28} strokeWidth={1.5} />,
            status: "Prototype"
        },
        { 
            title: "Project S: Logistics AI", 
            description: "Predictive demand forecasting and autonomous inventory optimization models.", 
            icon: <Cpu size={28} strokeWidth={1.5} />,
            status: "Research"
        },
    ];

    // Animation Variants
    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
    };

    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20, filter: 'blur(2px)' },
        visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease: "easeOut" } },
    };

    const lineVariant: Variants = {
        hidden: { width: 0 },
        visible: { width: "100%", transition: { duration: 1, ease: "easeInOut" } }
    };

    return (
        <div className={styles.portfolioPage}>

            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroBadgeWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Ecosystem Overview
                            </motion.span>
                        </motion.div>

                        <motion.h1 
                            className={styles.heroTitle} 
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.dBlock}>
                                Engineering the 
                            </motion.span>
                            <motion.span variants={itemVariants} className={styles.dBlock}>
                                <span className={styles.titleHighlight}>Structural Integrity</span>
                            </motion.span>
                            <motion.span variants={itemVariants} className={styles.dBlock}>
                                of Business.
                            </motion.span>
                        </motion.h1>
                        
                        <motion.p 
                            className={styles.heroSubtitle} 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            transition={{ delay: 0.6, duration: 0.8 }}
                        >
                            We deploy capital and code into ventures that solve foundational complexity.
                            From ERP monoliths to agile AI microservices.
                        </motion.p>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. FLAGSHIP PRODUCT: OTA ERP */}
            <AnimatedSection className={styles.productSection}>
                <div className={styles.container}>
                    <motion.div className={styles.productHeader} variants={itemVariants}>
                        <div className={styles.headerTopRow}>
                            <span className={styles.sectionLabel}>Flagship Venture</span>
                            <span className={styles.statusPill}>Status: Beta</span>
                        </div>
                        <h2 className={styles.sectionTitle}>OTA ERP</h2>
                        <p className={styles.sectionSubtitle}>
                            The unified operational platform designed to replace fragmented legacy systems. 
                            Built for speed, accuracy, and absolute data consistency.
                        </p>
                    </motion.div>
                    
                    <motion.div className={styles.dividerLine} variants={lineVariant} />

                    <motion.div className={styles.featureGrid} variants={containerVariants}>
                        {otaErpFeatures.map((feature, index) => (
                            <motion.div key={index} className={styles.featureCard} variants={itemVariants}>
                                {/* UPDATED LAYOUT: Row for Icon + Title */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.iconWrapper}>{feature.icon}</div>
                                    <h3 className={styles.featureTitle}>{feature.title}</h3>
                                </div>
                                <p className={styles.featureDesc}>{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                    
                    <motion.div className={styles.launchCta} variants={itemVariants}>
                        <a 
                            href="https://www.ota-erp.com" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.ctaButton}
                        >
                            Visit Platform <ArrowUpRight size={18} />
                        </a>
                        <span className={styles.launchNote}>Global Launch Q2 2026</span>
                    </motion.div>
                </div>
            </AnimatedSection>
            
            {/* 3. FUTURE VENTURES */}
            <AnimatedSection className={styles.futureSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCentered}>
                        <span className={styles.sectionLabel}>R&D Horizon</span>
                        <motion.h2 className={styles.sectionTitle} variants={itemVariants}>Future Ventures</motion.h2>
                    </div>

                    <motion.div className={styles.futureGrid} variants={containerVariants}>
                        {futureVentures.map((venture, index) => (
                            <motion.div key={index} className={styles.futureCard} variants={itemVariants}>
                                <div className={styles.futureCardHeader}>
                                    <div className={styles.futureIconBox}>{venture.icon}</div>
                                    <span className={styles.researchBadge}>{venture.status}</span>
                                </div>
                                <h3 className={styles.futureTitle}>{venture.title}</h3>
                                <p className={styles.futureDesc}>{venture.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                    
                    <motion.div className={styles.contactCta} variants={itemVariants}>
                        <Link to="/investors" className={styles.linkButton}>
                            Access Investment Dossier <ArrowRight size={16} />
                        </Link>
                    </motion.div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default PortfolioPage;